import { DEFAULT_THEME_COLOR } from '~/libs/customizations'
import { defineStore } from 'pinia'
import { localStorageService } from '~/libs/local-storage.service'
import { ulid as getUlid } from 'ulid'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useAppAnalyticsStore } from '~/store/app-analytics'
import { useBorrowerStore } from '~/store/borrower'
import { useCobrandStore } from '~/store/cobrand'
import { useIdentityStore } from '~/store/identity'
import { useQuestionsStore } from '~/store/questions'
import { useUserStore } from '~/store/user'
import env from '~/libs/env'
import PublicLenders from '~/libs/mocks/publicLenders.json'
import TimeoutWorker from '~/src/workers/timeoutWorker?worker&inline'
import TrackingService from '~/libs/tracking-service'
import VisitorTracker from '~/libs/visitor-tracker'
import { get, zipObject } from 'es-toolkit/compat'
import appConstants from '~/libs/constants/application-constants.js'

const { nuxtBaseUrl } = appConstants

export const useRootStore = defineStore('root', () => {
  const { $lendioCookies, $axios, $config } = useNuxtApp()
  const router = useRouter()
  const appAnalyticsStore = useAppAnalyticsStore()
  const borrowerStore = useBorrowerStore()
  const cobrandStore = useCobrandStore()
  const userStore = useUserStore()
  const questionsStore = useQuestionsStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const identityStore = useIdentityStore()

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/

  const isCypressUser = ref(null) // null | boolean
  const isGuest = ref(null)
  const activationId = ref('')
  const anonymousId = ref(null)
  const authUser = ref(null)
  const authToken = ref(null)
  const authTokenExpiry = ref(null)
  const expiryTimeoutSet = ref(false)
  const inProgress401 = ref(false)
  const displayExpiryModal = ref(false)
  const affiliateId = ref(null)
  const isEmbedded = ref(null)
  const styleId = ref(null)
  const embeddedJwt = ref(null)
  const intercomHash = ref(null)
  const pageTitle = ref('Lendio')
  const lendioFaviconsActive = ref(false)
  const favicons = ref([
    {
      rel: 'shortcut icon',
      href: 'images/blank-favicon.ico',
      type: 'image/x-icon'
    }
  ])
  const dynamicAppProgress = ref({})
  const renewalAppProgress = ref({})
  const currentPage = ref(null)
  const marketingOwnership = ref(null)
  const validMarketingOwnershipResponse = ref(false)
  const currentBorrowerInterest = ref(null)
  const publicLenderList = ref([])
  const useLegacyDesign = ref(true)
  const testing = ref('testing-value')
  const pwfSignupInProgress = ref(false)
  const visitorId = ref(null)

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/

  const getEmbeddedInitBody = computed(() => {
    const jwt = embeddedJwt.value
    const user = authUser.value

    if (!user || !jwt) {
      return null
    }

    return {
      jwt: jwt,
      user: {
        login: user.email,
      },
    }
  })

  const canUseEmbedded = computed(() => {
    return authUser.value && !isEmbedded.value && embeddedJwt.value
  })

  const getPublicLenderList = computed(() => {
    return publicLenderList.value
  })

  const getAffiliateId = computed(() => {
      return affiliateId
  })

  const userProfile = computed(() => {
    const _authUser = authUser.value
    if (_authUser) {
      return _authUser
    }
    return null
  })

  const isLoggedIn = computed(() => {
    return !!authToken.value && !!authUser.value
  })

  const hasMultipleBorrowers = computed(() => {
    return authUser.value?.borrowers?.length > 1
  })

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/

  function trimStringQuotes(val) {
    // Cookies should always be saved as strings
    const str = typeof val === 'string'
      ? val
      : JSON.stringify(val)

    // Guard against strings that have been encoded twice
    // This fixed an auth issue where the token was being
    // saved as a cookie with encoded quotes but only for
    // a specific deauthentication event
    const match = str.match(/^"(.*)"$/)
    if (match) {
      const [_, contents] = match
      return contents
    }

    return str
  }

  /**
   * @param {string} token
   * @returns {Promise<void>}
   */
  async function setAuthToken(token) {
    authToken.value = token

    if (token && await $lendioCookies.get('token') !== token) {
      await $lendioCookies.set('token', this.trimStringQuotes(token), { path: isEmbedded.value ? `${nuxtBaseUrl}/embedded` : `${nuxtBaseUrl}/`, expires: 0.5 })

      try {
        const expiration = JSON.parse(atob(token.split('.')[1])).exp
        authTokenExpiry.value = expiration
      } catch (e) {
        log.warning('Error parsing Lendio JWT', e)
      }
    }
  }

  function setVisitorId(_visitorId) {
    visitorId.value = _visitorId
  }

  function setInProgress401(_inProgress) {
    inProgress401.value = _inProgress
  }

  async function setIsEmbedded(_isEmbedded) {
    isEmbedded.value = _isEmbedded
    if (_isEmbedded && $lendioCookies.get('isEmbedded') !== _isEmbedded) {
      await $lendioCookies.set('isEmbedded', _isEmbedded, { path: _isEmbedded ? `${nuxtBaseUrl}/embedded` : `${nuxtBaseUrl}/`, expires: 0.5 })
    }
  }

  async function setStyleId(_styleId) {
    styleId.value = _styleId
    if (_styleId && $lendioCookies.get('style_id') !== _styleId) {
      await $lendioCookies.set('style_id', _styleId, { path: isEmbedded.value ? `${nuxtBaseUrl}/embedded` : `${nuxtBaseUrl}/`, expires: 0.5 })
    }
  }

  async function setIntercomHash(value)
  {
    intercomHash.value = value
    if (value && await $lendioCookies.get('intercom_hash') !== value) {
      await $lendioCookies.set('intercom_hash', this.trimStringQuotes(value), { path: `${nuxtBaseUrl}/`, expires: 1 })
    }
  }

  function getAnonymousId(forceSet = false) {
    const _anonymousId = anonymousId.value || $lendioCookies.get('anonymousId')
    if (!forceSet && _anonymousId) {
      return _anonymousId
    }

    const ulid = _anonymousId || getUlid()
    $lendioCookies.set('anonymousId', ulid, { expires: 90 })
    anonymousId.value = ulid
    return ulid
  }

  async function getIntercomHash(payload = {})
  {
    if (env('environment') !== 'LIVE' && !env('debugChat')) {
      return
    }

    if (intercomHash.value && !payload.forceReload) return intercomHash.value;

    // We must have either a user or lendioId to get a hash
    if (!authUser && !appAnalyticsStore.getLendioId) return;

    let path = '/intercom/gethash/'

    if (!authUser) {
      path.concat(appAnalyticsStore.getLendioId)
    }

    return await $axios.get(`${env('apiUrl')}${path}`)
      .then(res => {
        const resData = get(res, 'data.data')
        intercomHash.value = resData;
        return resData
      })
      .catch(err => {
        log.warning('[Intercom] - Error Fetching Intercom API hash.')
      })
  }

  function reset() {
    authUser.value = null
    authToken.value = null
    authTokenExpiry.value = null
    embeddedJwt.value = null
    intercomHash.value = null
    anonymousId.value = null
  }

  async function getTokenExpiration() {
    const Authorization = userStore.getAuthHeader()

    if (!Authorization || !authToken.value) {
      return null
    }

    try {
      const expiration = JSON.parse(atob(authToken.value.split('.')[1])).exp
      authTokenExpiry.value = expiration

      return authTokenExpiry.value
    } catch (e) {
      log.warning('Error parsing Lendio JWT', e)
    }
  }

  async function setupTokenExpirationTimeout({ expiryArg = null, delayStart = 0 } = {}) {
    // Option to delay the start of method execution to allow the
    // extend sesssion job time to complete prior to checking expiration.
    // delayStart should be provided as value in seconds.
    if (delayStart) {
      const delayStartMs = delayStart * 1000

      expiryTimeoutSet.value = true
      const delayTimeoutWorker = new TimeoutWorker()
      delayTimeoutWorker.onmessage = () => {
        expiryTimeoutSet.value = false
        delayTimeoutWorker.terminate()
        this.setupTokenExpirationTimeout({ expiryArg })
      }
      delayTimeoutWorker.postMessage(delayStartMs)

      return
    }
    const expiry = expiryArg ?? await this.getTokenExpiration()
    if (!expiry) { return false }

    // Formatted TS to get delay MS for setTimeout
    const showModalDate = new Date(expiry * 1000)
    const subMinutes = 6
    showModalDate.setMinutes(showModalDate.getMinutes() - subMinutes)
    const showModalDateTs = showModalDate.getTime()
    const nowTs = Date.now()
    // Ensure that if they click continue session we wait up to minimum time before checking again
    const minimumDelayMs = 30 * 1000
    const delay = Math.max(minimumDelayMs, showModalDateTs - nowTs)

    expiryTimeoutSet.value = true
    const coreTimeoutWorker = new TimeoutWorker()
    // Evaluation function for setTimeout and modal trigger
    coreTimeoutWorker.onmessage = async () => {
      expiryTimeoutSet.value = false
      coreTimeoutWorker.terminate()
      const callbackExpiry = await this.getTokenExpiration()
      if (callbackExpiry && callbackExpiry > expiry) {
        this.setupTokenExpirationTimeout({ expiryArg: callbackExpiry })
      } else {
        this.setDisplayExpiryModal(true)
      }
    }
    coreTimeoutWorker.postMessage(delay)

    return expiryTimeoutSet.value
  }

  function setDisplayExpiryModal(value) {
    if (displayExpiryModal.value === value) {
      return
    }
    displayExpiryModal.value = value
  }

  async function authenticate({ login = false, redirect = false } = {}) {
    if (!authToken.value) {
      return
    }

    const user = await this.getCurrentUser({ forceReload: true, login })

    if (!user) {
      return
    }

    const borrowers = user?.borrowers ?? []

    if (borrowers.length < 1) {
      throw new Error('User has no associated businesses')
    }

    const borrowerId = borrowers.length === 1
      ? borrowers[0].id
      : borrowerStore.getOwnedCookieBorrowerId()

    if (!borrowerId && borrowers.length > 1) {
      return router.push(identityStore.businessSelectUrl)
    }

    await borrowerStore.getBorrower({ borrowerId, touch: login })

    if (redirect) {
      return router.push(this.getRedirectUrl())
    }

    return
  }

  /**
   * Pull activationId from localStorage and set them into state
   */
  function getActivationIdFromStorage() {
    // localStorageService handles checking for existence of the service and
    // falls back to saving in an object, allowing us to remove the check that
    // was previously here: `if(typeof localStorage === 'undefined') return`.
    const _activationId = localStorageService.getItem('activationId')
    // set activationId in store
    if (_activationId) {
      activationId.value = _activationId
    }
  }

  async function getCurrentUser({ forceReload = false, login = false } = {}) {
    if (!authToken.value) {
      return
    }

    if (authUser.value && !forceReload) {
      return authUser.value
    }

    try {
      const response = await $axios.get(`${env('apiUrl')}/borrower-portal/user`)
      const data = response?.data
      const status = response?.status

      if (authToken.value && status === 200 && data?.data) {
        authUser.value = data.data
        affiliateId.value = get(data, 'data.marketingData.affiliateId')
        const borrowers = authUser.value.borrowers || []
        const borrowerId = borrowers.length === 1 ? borrowers[0].id : borrowerStore.getOwnedCookieBorrowerId()
        const touchOnLogin = login && borrowerId && !borrowerStore.isBorrowerLoaded(borrowerId)

        const results = await Promise.allSettled([
          identityStore.getAuthUserIdP(),
          identityStore.checkForSetPassword(),
          borrowerId ? borrowerStore.getBorrower({ borrowerId, touch: false }) : null,
        ])
        const failed = results.find(result => result.status === 'rejected')

        if (failed) {
          throw failed.reason
        }

        if (login) {
          await $axios.post(`${env('apiUrl')}/user/last-login`)

          if (touchOnLogin) {
            await borrowerStore.addLastTouch()
          }
        }

        return authUser.value
      }

      let errorLog = {
        message: '[IDENT] rootStore.getCurrentUser failed unexpectedly',
        data: {
          tokenClaims: null,
          status: status || 'no status',
          responseData: data?.data || 'no response data',
        },
      }

      try {
        const decodedJwt = JSON.parse(atob(authToken.value.split('.')[1]))
        errorLog.data.tokenClaims = {
          clientId: decodedJwt.cid ?? 'no cid claim',
          identityPublicId: decodedJwt.ipd ?? 'no ipd claim',
          tenantId: decodedJwt.tid ?? 'no tid claim',
          email: decodedJwt.lid ?? 'no lid claim',
        }
      } catch (e) {
        errorLog = {
          message: '[IDENT] getCurrentUser failed and the auth token could not be parsed',
          data: { ...errorLog.data, tokenParseError: e }
        }
      }

      if (status !== 401) {
        log.error(errorLog.message, errorLog.data)
      }

      await identityStore.logout()
    } catch (error) {
      if (error?.response?.status !== 401) {
        log.error('Error fetching current user', error)
      }

      await identityStore.logout()
    }

    return null
  }

  function getRedirectUrl() {
    let redirect = $lendioCookies.get('redirect')

    if (redirect) {
      $lendioCookies.remove('redirect')
      redirect = redirect.startsWith('/') ? redirect : '/' + redirect
      redirect = cleanOldRedirectPath(redirect)
    }

    return redirect ?? '/resume-app'
  }

  /**
   * Make request for current borrower interest, saving current borrower interest to state
   * @returns {Promise.<void>}
   */
  async function postCurrentBorrowerInterest(interest = 'marketplace') {
    const borrower = borrowerStore.borrower
    const Authorization = userStore.getAuthHeader()
    if (!borrower || !borrower.id || !Authorization) {
      return
    }
    const { data } = await $axios.post(
      `${env('apiUrl')}/borrower/${borrower.id}/interest`,
      interest,
      {
        headers: { Authorization }
      }
    )
    const _currentBorrowerInterest = get(data, 'data', null)
    currentBorrowerInterest.value = _currentBorrowerInterest
  }

  async function getMarketingOwnership({ forceReload = false } = {}) {
    if (pwfSignupInProgress.value || !borrowerStore.borrower?.id) return

    if (!forceReload && (marketingOwnership.value || validMarketingOwnershipResponse.value)) {
      return marketingOwnership.value
    }

    try {
      const response = await $axios.get(`${env('apiUrl')}/borrower/${borrowerStore.borrower?.id}/marketing/ownership`)
      const data = response?.data
      if (get(data, 'statusCode', null) === 200) {
        validMarketingOwnershipResponse.value = true
      }

      return this.setMarketingRecord(get(data, 'data', null))
    } catch (error) {
      log.error('Error during ${borrowerId}/marketing/ownership. ', error)
    }
  }

  function setMarketingRecord(marketing) {
    cobrandStore.setAffiliateCobrand(
      get(marketing, 'affiliateId')
    )

    marketingOwnership.value = marketing

    return marketing
  }

  async function loginTokenSignIn (query) {
    pwfSignupInProgress.value = true

    if (VisitorTracker.anyTrackedCookiesSet($lendioCookies)) {
      $lendioCookies.set('skipMarketingRecord', false)
    }

    const cookies = $lendioCookies.getTrackingCookies()
    const cobrandAffiliateId = get(cobrandStore, 'config.affiliateId', $lendioCookies.get('cobrand'))

    if (
      cobrandAffiliateId &&
      (!cookies.affiliate ||
        /^(null|undefined|NaN|0|false)$/i.exec(cookies.affiliate))
    ) {
      cookies.affiliate = cobrandAffiliateId
    }

    const {
      e: email,
      t: token,
      r: route,
      touchpoint,
      selfselect,
      tokenType,
      borrowerId,
    } = query

    // workaround to fix url encoding replacing + with space
    const emailEncode = email?.replace(/\s/g, '+')
    let tokenData = {
      username: emailEncode,
      password: token,
      tokenType: tokenType,
      touchpoint: touchpoint,
      cookies: JSON.stringify(cookies),
      skipMarketingRecord: $lendioCookies.get('skipMarketingRecord') === 'true'
    }

    if (selfselect) {
      tokenData.selfSelect = selfselect
    }

    const trackedCookies = VisitorTracker.trackedValues($lendioCookies)
    const trackedKeys = Object.keys(trackedCookies)
    const trackedValues = zipObject(
      trackedKeys,
      trackedKeys.map((key) => cookies[key] || trackedCookies[key])
    )
    Object.assign(tokenData, trackedValues)
    localStorageService.setItem('tokenData', JSON.stringify(tokenData))
    localStorageService.setItem('query', JSON.stringify(query))
    await this.postTokenLoginActions()
    localStorageService.setItem('pwfSignup', 'true')
  }

  async function postTokenLoginActions() {
    const query = JSON.parse(localStorageService.getItem('query'))
    const tokenData = JSON.parse(localStorageService.getItem('tokenData'))
    const {
      r: redirect,
      ref: referral,
      franchiseId,
      visitor: queryVisitorId,
      t: token,
      tokenType,
      borrowerId
    } = query

    if (borrowerId) {
      $lendioCookies.set('borrowerId', borrowerId, { path: `${nuxtBaseUrl}/`, expires: 0.5 })
    }

    const passThroughParams = {
      ref: referral || franchiseId
    }
    let tokenFormData = new FormData()

    for (let key in tokenData) {
      tokenFormData.append(key, tokenData[key])
    }

    this.setVisitorId(queryVisitorId)

    if (tokenType !== 'lendio_jwt')  {
      throw new Error(`Tried to perform PWF login, but tokenType was ${tokenType}`)
    }
    await identityStore.tokenLogin(token)
    questionsStore.updateMlWhitelist()
    $lendioCookies.clearTrackingCookies()

    // contactOption is determined by api borrower contactOption
    if (get(authUser.value, 'contactOption') === true) {
      $lendioCookies.set('contactOption', true, { expires: 7 })
    }

    if (get(authUser.value, 'lastTrack') !== false) {
      $lendioCookies.set('skipMarketingRecord', true, { expires: 7 })
    }

    TrackingService.onLogin(get(borrowerStore.borrower?.id), $lendioCookies)

    // Clear search params
    router
      .replace({ query: { e: null, r: null, t: null } })
      .catch((err) => {}) //catch potential errors rather than spamming rollbar

    // add the tier param
    if (borrowerStore.borrower?.tier) {
      passThroughParams.tier = borrowerStore.borrower.tier
    }

    // add the mineral group
    const mineralGroup = get(authUser.value, 'marketingData.mineralGroup', null)

    if (mineralGroup) {
      passThroughParams.mineral = mineralGroup.toLowerCase()
    }

    if (borrowerStore.isEligibleForExpectationsStoryArc) {
      return navigateTo('/next-steps/funding-expert')
    }

    if (redirect && redirect !== '/app') {
      const cleanRedirect = cleanOldRedirectPath(redirect)
      return navigateTo(cleanRedirect)
    }

    return navigateTo({ path: '/resume-app', query: passThroughParams })
  }

  function setDynamicAppProgress(progress) {
    dynamicAppProgress.value = progress
    localStorageService.setItem(
      'dynamicAppProgress',
      dynamicAppProgress.value
    )
  }

  function setPWFSignupStatus(status) {
    pwfSignupInProgress.value = status
  }

  // Renewal App progress
  function setRenewalAppProgress(progress) {
    renewalAppProgress.value = progress
    localStorageService.setItem(
      'renewalAppProgress',
      renewalAppProgress.value
    )
  }

  function saveRenewalProgress(progress) {
    let progressObject = renewalAppProgress.value
    progressObject[progress.page] = progress.step
    this.setRenewalAppProgress(processObject)
  }

  function saveProgress(progress) {
    let progressObject = dynamicAppProgress.value
    progressObject[progress.page] = progress.step
    this.setDynamicAppProgress(progressObject)
  }

  function updateCurrentPage(_currentPage) {
    currentPage.value = _currentPage
  }

  async function requestPublicLenderList() {
    if (publicLenderList.value.length) return publicLenderList.value
    let res = await $axios
      .get(`${env('apiUrl')}/public/lender`)
      .catch((err) => {
        log.error('Error fetching stats', { err })
      })
    // Switch commented lenderList for local testing as needed
    const lenderList = PublicLenders.data
    // const lenderList = get(res, 'data.data', null) || []
    publicLenderList.value = lenderList
    return publicLenderList.value
  }

  function setPageTitle(string) {
    pageTitle.value = string
  }

  function setLendioFavicons() {
    lendioFaviconsActive.value = true
    let faviconPath = 'images/'
    let ink = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? '' : '-dark'
    if (affiliateCustomizationStore.useCustomFavicon && affiliateCustomizationStore.key) {
      faviconPath = `https://cdn.lendio.com/tenant/${affiliateCustomizationStore.key}/`
      ink = ''
    }

    favicons.value = [
      {
        rel: 'icon',
        type: 'image/png',
        href: `${faviconPath}favicon${ink}-32x32.png`
      },
      {
        rel: 'icon',
        type: 'image/png',
        href: `${faviconPath}favicon${ink}-16x16.png`
      },
      {
        rel: 'mask-icon',
        href: faviconPath + 'safari-pinned-tab.svg',
        color: DEFAULT_THEME_COLOR
      },
      {
        rel: 'shortcut icon',
        href: `${faviconPath}favicon${ink}.ico`
      },
      {
        rel: 'icon',
        href: `${faviconPath}favicon${ink}.ico`,
        type: 'image/x-icon'
      }
    ]
  }

  function setBlankFavicons() {
    lendioFaviconsActive.value = false
    favicons.value = [
      {
        rel: 'shortcut icon',
        href: 'images/blank-favicon.ico',
        type: 'image/x-icon'
      }
    ]
  }

  function watchColorScheme() {
    const query = typeof window === 'undefined' ? null : window.matchMedia?.('(prefers-color-scheme: dark)')
    query?.addEventListener('change', () => {
      if (lendioFaviconsActive.value) setLendioFavicons()
    })
  }

  watchColorScheme()

  function setAffiliateId(_affiliateId) {
    affiliateId.value = _affiliateId
  }

  function getAuthTokenExpiry() {
    return authToken.value
      ? JSON.parse(atob(authToken.value.split('.')[1])).exp
      : null
  }

  function loadIsGuest(secureLinkVal = null) {
    const guestInviteToken = secureLinkVal || $lendioCookies.get('secureLink') || $lendioCookies.get('documentUploadRequestInviteToken')
    isGuest.value = (!!guestInviteToken && !authToken.value)
  }

  return {
    // STATE
    activationId,
    affiliateId,
    anonymousId,
    authToken,
    authTokenExpiry,
    authUser,
    authenticate,
    currentBorrowerInterest,
    currentPage,
    displayExpiryModal,
    dynamicAppProgress,
    embeddedJwt,
    expiryTimeoutSet,
    favicons,
    inProgress401,
    intercomHash,
    isCypressUser,
    isEmbedded,
    isGuest,
    styleId,
    marketingOwnership,
    pageTitle,
    publicLenderList,
    pwfSignupInProgress,
    renewalAppProgress,
    testing,
    useLegacyDesign,
    visitorId,
    // GETTERS
    canUseEmbedded,
    getAffiliateId,
    getEmbeddedInitBody,
    getPublicLenderList,
    hasMultipleBorrowers,
    isLoggedIn,
    userProfile,
    // ACTIONS
    reset,
    getActivationIdFromStorage,
    getAnonymousId,
    getAuthTokenExpiry,
    getCurrentUser,
    getIntercomHash,
    getMarketingOwnership,
    getRedirectUrl,
    getTokenExpiration,
    loadIsGuest,
    loginTokenSignIn,
    postCurrentBorrowerInterest,
    postTokenLoginActions,
    requestPublicLenderList,
    saveProgress,
    saveRenewalProgress,
    setAffiliateId,
    setAuthToken,
    setBlankFavicons,
    setDisplayExpiryModal,
    setDynamicAppProgress,
    setInProgress401,
    setIntercomHash,
    setIsEmbedded,
    setLendioFavicons,
    setMarketingRecord,
    setPageTitle,
    setStyleId,
    setPWFSignupStatus,
    setRenewalAppProgress,
    setupTokenExpirationTimeout,
    setVisitorId,
    trimStringQuotes,
    updateCurrentPage,
  }
})
